import { PIPEDB_URL } from './pipedb-url';

/**
 * Crash reports to the API (POST /client-errors), which writes each as one
 * `client-error` trace in App Insights (SF-92).
 *
 * A crash on a client's phone used to leave a blank page and nothing else: no
 * request failed, so nothing was logged anywhere we could look. Now a crash is
 * sent on its own, and the error screen lets the person add what they were
 * doing under the same reference.
 *
 * Never throws and never retries: reporting must not become a second failure.
 */

export type ReportKind = 'crash' | 'report';

export interface ErrorReport {
  kind: ReportKind;
  /** Shown on the error screen, so a person can quote it to their coach. */
  id: string;
  message?: string;
  stack?: string;
  note?: string;
}

/** Eight characters. Not crypto.randomUUID: Safari only has it from iOS 15.4. */
export const newReportId = (): string =>
  Math.random().toString(36).slice(2, 10).padEnd(8, '0');

/** Origin and path. A share link's code is in the query, and stays here. */
export const pageForReport = (href: string): string => {
  try {
    const u = new URL(href);
    return `${u.origin}${u.pathname}`;
  } catch {
    return href.split(/[?#]/)[0];
  }
};

const describe = (error: unknown): { message: string; stack?: string } => {
  if (error instanceof Error) {
    return { message: `${error.name}: ${error.message}`, stack: error.stack };
  }
  return { message: typeof error === 'string' ? error : String(error) };
};

export async function sendErrorReport(report: ErrorReport): Promise<boolean> {
  try {
    const res = await fetch(`${PIPEDB_URL}/client-errors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // Survives the reload the error screen offers.
      keepalive: true,
      body: JSON.stringify({
        ...report,
        message: report.message?.slice(0, 500),
        stack: report.stack?.slice(0, 4000),
        note: report.note?.slice(0, 1000),
        page: pageForReport(window.location.href),
        commit: typeof __APP_COMMIT__ === 'string' ? __APP_COMMIT__.slice(0, 12) : undefined,
        userAgent: navigator.userAgent,
      }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/** Per page load: a crash loop reports a few times, not hundreds. */
export const MAX_AUTOMATIC = 5;
let automatic = 0;
const seen = new Set<string>();

export const resetCrashReporting = () => {
  automatic = 0;
  seen.clear();
};

/**
 * Send a crash once per distinct message. Returns the report's id, or null
 * when it was a repeat or over the per-load cap and nothing was sent.
 *
 * `onScreen` is the error screen's own report, sent even when the same error
 * already went out: its id is the one the person sees, and React in
 * development also hands a caught render error to window's error listener
 * first.
 */
export function reportCrash(
  error: unknown,
  id: string = newReportId(),
  onScreen = false,
): string | null {
  const { message, stack } = describe(error);
  if (!onScreen && (seen.has(message) || automatic >= MAX_AUTOMATIC)) return null;
  seen.add(message);
  automatic++;
  void sendErrorReport({ kind: 'crash', id, message, stack });
  return id;
}

/**
 * Errors React never sees: event handlers, timers, and promises nobody
 * awaited — the "Uncaught (in promise)" that only showed in a console.
 */
export function installCrashReporting(): void {
  window.addEventListener('error', (e) => {
    // Resource load failures reach window without an error object; the
    // missing image or font is not a crash.
    if (e.error || e.message) reportCrash(e.error ?? e.message);
  });
  window.addEventListener('unhandledrejection', (e) => reportCrash(e.reason));
}
